import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { resolveMediaUrl } from "@/lib/api";

const ASPECT_CLASS = {
  auto: "",
  landscape: "aspect-[16/10]",
  square: "aspect-square",
  portrait: "aspect-[3/4]",
};

/** Ukuran modal — jauh lebih besar agar banner jelas terbaca */
const SIZE_CLASS = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-5xl",
  xl: "max-w-6xl",
};

const WelcomePopup = () => {
  const { settings } = useSettings();
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const popup = settings?.popup;
  const onHome = pathname === "/";
  const canShow = Boolean(
    onHome &&
    popup?.enabled &&
    (popup.image || popup.title || popup.description)
  );

  // Langsung buka begitu data siap (cache/localStorage membuat ini hampir instan saat reload)
  const [open, setOpen] = useState(canShow);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!onHome) {
      setOpen(false);
      setClosed(false);
      return;
    }
    if (canShow && !closed) {
      setOpen(true);
    }
  }, [canShow, onHome, closed]);

  // Preload gambar agar tampil cepat
  useEffect(() => {
    if (!popup?.image) return;
    const img = new Image();
    img.src = resolveMediaUrl(popup.image);
  }, [popup?.image]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setClosed(true);
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setClosed(true);
  };

  if (!open || !canShow) return null;

  const image = resolveMediaUrl(popup.image || "");
  const link = (popup.button_link || "").trim();
  const btnText = (popup.button_text || t("popup.defaultButton")).trim();
  const isExternal = /^https?:\/\//i.test(link);
  const hasCopy = Boolean(popup.title || popup.description || (link && btnText));
  const isContain = popup.image_fit === "contain";
  const aspectKey = popup.image_aspect || "landscape";
  // Mode tanpa crop: pakai rasio asli supaya gambar memenuhi area besar tanpa letterbox kaku
  const effectiveAspect = isContain ? "auto" : aspectKey;
  const aspect = Object.prototype.hasOwnProperty.call(ASPECT_CLASS, effectiveAspect)
    ? ASPECT_CLASS[effectiveAspect]
    : ASPECT_CLASS.landscape;
  const fit = isContain ? "object-contain bg-zinc-950/5" : "object-cover";
  // Utuh tanpa crop → hampir fullscreen; selain itu ikuti pilihan ukuran admin
  const size = isContain
    ? "max-w-[min(94vw,80rem)]"
    : SIZE_CLASS[popup.popup_size] || SIZE_CLASS.md;
  const imageMaxH = hasCopy ? "max-h-[min(72vh,720px)]" : "max-h-[min(88vh,900px)]";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label={popup.title || t("common.announcement")}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        aria-label={t("common.close")}
        onClick={close}
      />
      <div
        className={`relative w-full ${size} max-h-[min(94vh,960px)] bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col`}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/95 text-zinc-700 flex items-center justify-center shadow-md hover:bg-zinc-100 transition"
          aria-label={t("common.close")}
        >
          <X size={18} />
        </button>

        {image ? (
          <div
            className={`bg-zinc-100 overflow-hidden flex items-center justify-center shrink min-h-0 ${
              aspect || imageMaxH
            }`}
          >
            <img
              src={image}
              alt={popup.title || t("common.promo")}
              className={`w-full ${
                aspect ? `h-full ${fit}` : `h-auto ${imageMaxH} ${fit}`
              }`}
              fetchPriority="high"
            />
          </div>
        ) : null}

        {hasCopy ? (
          <div className="p-5 sm:p-7 shrink-0">
            {popup.title ? (
              <h2 className="text-xl sm:text-2xl font-display font-bold text-zinc-900 tracking-tight text-balance">
                {popup.title}
              </h2>
            ) : null}
            {popup.description ? (
              <p className={`text-zinc-600 leading-relaxed whitespace-pre-line ${popup.title ? "mt-3" : ""}`}>
                {popup.description}
              </p>
            ) : null}

            {link && btnText ? (
              <div className="mt-5">
                {isExternal ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    className="inline-flex w-full items-center justify-center px-6 py-3.5 rounded-full bg-maroon-800 hover:bg-maroon-900 text-white font-semibold transition"
                  >
                    {btnText}
                  </a>
                ) : (
                  <Link
                    to={link.startsWith("/") ? link : `/${link}`}
                    onClick={close}
                    className="inline-flex w-full items-center justify-center px-6 py-3.5 rounded-full bg-maroon-800 hover:bg-maroon-900 text-white font-semibold transition"
                  >
                    {btnText}
                  </Link>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WelcomePopup;
